import { useEffect, useMemo, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Divider, Chip, Grid, MenuItem } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

const formatDateForPicker = (value) => (value ? moment(value, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"], true) : null);

export default function EditUtilityLicenseV2Form({ open, handleClose, rowData, fetchData }) {
    const [editData, setEditData] = useState(null);
    const [errors, setErrors] = useState({});
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const requiredFields = useMemo(() => ["product_code", "customer_name", "expires_at"], []);
    const statusOptions = useMemo(
        () => [
            { value: "active", label: "Active" },
            { value: "suspended", label: "Suspended" },
            { value: "revoked", label: "Revoked" },
        ],
        []
    );
    const deviceStatusOptions = useMemo(
        () => [
            { value: "active", label: "Active" },
            { value: "released", label: "Released" },
            { value: "revoked", label: "Revoked" },
        ],
        []
    );

    useEffect(() => {
        if (rowData && open) {
            setEditData({
                id: rowData.id,
                remarks: rowData.remarks || "",
                product_code: rowData.product_code || "",
                customer_code: rowData.customer_code || "",
                customer_name: rowData.customer_name || "",
                customer_email: rowData.customer_email || "",
                status: rowData.status || "active",
                device_status: rowData.device_status || "",
                expires_at: formatDateForPicker(rowData.expires_at),
                offline_days: Number(rowData.offline_days || 30),
            });
            setErrors({});
            setHelperText({});
            setErrorMessage("");
        }
    }, [open, rowData]);

    if (!rowData || !editData) {
        return null;
    }

    const setError = (field, message) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const clearError = (field) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
        setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
    };

    const validate = (field, value) => {
        const isRequired = requiredFields.includes(field);

        if (isRequired && (!value || value === "")) {
            setError(field, "This field is required.");
            return false;
        }

        if (field === "customer_email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            setError(field, "Please enter a valid email address.");
            return false;
        }

        clearError(field);
        return true;
    };

    const handleEdit = async () => {
        let isValid = true;

        requiredFields.forEach((field) => {
            const value = field === "expires_at" ? (editData.expires_at ? moment(editData.expires_at).format("YYYY-MM-DD") : "") : editData[field];
            if (!validate(field, value)) {
                isValid = false;
            }
        });

        if (!validate("customer_email", editData.customer_email || "")) {
            isValid = false;
        }

        if (!isValid) {
            setErrorMessage("Please fill all required fields correctly.");
            return;
        }

        setErrorMessage("");

        try {
            const payload = {
                product_code: editData.product_code,
                customer_code: editData.customer_code || undefined,
                customer_name: editData.customer_name,
                customer_email: editData.customer_email || undefined,
                status: editData.status,
                expires_at: moment(editData.expires_at).format("YYYY-MM-DD"),
                offline_days: editData.offline_days,
            };
            if (editData.device_status) {
                payload.device_status = editData.device_status;
            }

            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v2/utility-license/${encodeURIComponent(editData.id)}`, payload);

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Utility license v2 information updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the utility license v2 information. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Utility License v2 Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Utility License v2 Details" />
                </Divider>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="remarks"
                            label="License Code"
                            type="text"
                            fullWidth
                            required
                            value={editData.remarks}
                            InputProps={{ readOnly: true }}
                            sx={{ backgroundColor: "#f1f0f0" }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="product_code"
                            label="Product Code"
                            type="text"
                            fullWidth
                            required
                            value={editData.product_code}
                            error={Boolean(errors.product_code)}
                            helperText={helperText.product_code}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setEditData({ ...editData, product_code: value });
                                validate("product_code", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_code"
                            label="Customer Code"
                            type="text"
                            fullWidth
                            value={editData.customer_code}
                            onChange={(event) => {
                                setEditData({ ...editData, customer_code: event.target.value || "" });
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_name"
                            label="Customer Name"
                            type="text"
                            fullWidth
                            required
                            value={editData.customer_name}
                            error={Boolean(errors.customer_name)}
                            helperText={helperText.customer_name}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setEditData({ ...editData, customer_name: value });
                                validate("customer_name", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_email"
                            label="Customer Email"
                            type="email"
                            fullWidth
                            value={editData.customer_email}
                            error={Boolean(errors.customer_email)}
                            helperText={helperText.customer_email}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setEditData({ ...editData, customer_email: value });
                                validate("customer_email", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="status"
                            label="Main Status"
                            select
                            fullWidth
                            value={editData.status}
                            onChange={(event) => {
                                setEditData({ ...editData, status: event.target.value });
                            }}
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                        autoComplete="off"
                        margin="dense"
                        id="device_status"
                        label="Device Status"
                        select
                        fullWidth
                        value={editData.device_status}
                        disabled={!editData.device_status}
                        helperText={!editData.device_status ? "No activation found for this license." : ""}
                        onChange={(event) => {
                            setEditData({ ...editData, device_status: event.target.value });
                        }}
                        >
                        {deviceStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Expires At"
                                inputFormat="YYYY-MM-DD"
                                value={editData.expires_at}
                                onChange={(newValue) => {
                                    setEditData({ ...editData, expires_at: newValue });
                                    validate("expires_at", newValue ? moment(newValue).format("YYYY-MM-DD") : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(errors.expires_at)}
                                        helperText={helperText.expires_at}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="offline_days"
                            label="Offline Days"
                            type="number"
                            fullWidth
                            value={editData.offline_days}
                            InputProps={{ readOnly: true }}
                            sx={{ backgroundColor: "#f1f0f0" }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleEdit} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Update
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
